import React from 'react';
import { StyleSheet } from 'react-native';
import { theme } from '@sg/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useFeatureFlagAsync } from '@order/LaunchDarkly';
import { useTrackEffect } from '@order/Telemetry';

import type { ChallengesScreenProps } from '../../navigation/AppNavigation.props';
import { ChallengesScreenContent } from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const ChallengesScreen = ({ navigation }: ChallengesScreenProps) => {
  const { value: isChallengesEnabled, isStale: isLoadingFeatureFlag } =
    useFeatureFlagAsync('OPS-challenges-enabled', {
      listenForChanges: true,
    });
  const { isFetchingCustomer } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const { startSignIn } = useAuthFlow();

  // ─────────────────────────────────────────────────────────────────

  const handlePressSignIn = React.useCallback(() => {
    startSignIn('challenges');
  }, [startSignIn]);

  // ─────────────────────────────────────────────────────────────────

  useTrackEffect('challenges.view');

  React.useEffect(() => {
    if (isLoadingFeatureFlag || isChallengesEnabled) return;
    navigation.navigate('HomeTab', { screen: 'Home' });
  }, [navigation, isLoadingFeatureFlag, isChallengesEnabled]);

  const isLoading = isFetchingCustomer || isLoadingFeatureFlag;

  // ─────────────────────────────────────────────────────────────────

  if (!isChallengesEnabled && !isLoadingFeatureFlag) {
    return null;
  }

  return (
    <PageWrapper
      testID="challenges.root.container"
      style={styles.pageWrapper}
      withHeaderTitle
    >
      <ChallengesScreenContent
        testID="challenges"
        isLoading={isLoading}
        isSignedIn={isLoggedIn}
        onPressSignIn={handlePressSignIn}
      />
    </PageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    flexGrow: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
});
